import React, { useState } from 'react';
import type { AppData } from '../data/apps';
import { DECISION_APPS, PRODUCTION_APPS } from '../data/apps';
import { AppCard } from './AppCard';

type FilterValue = 'all' | 'decision' | 'production';

interface AppGridProps {
  isLoggedIn: boolean;
  onOpenAuth: () => void;
}

export const AppGrid: React.FC<AppGridProps> = ({ isLoggedIn, onOpenAuth }) => {
  const [filter, setFilter] = useState<FilterValue>('all');

  const filters: { label: string; value: FilterValue }[] = [
    { label: 'All', value: 'all' },
    { label: 'Decision System', value: 'decision' },
    { label: 'Production System', value: 'production' },
  ];

  const showDecision = filter === 'all' || filter === 'decision';
  const showProduction = filter === 'all' || filter === 'production';

  return (
    <section id="apps" className="fs-page" style={{ paddingTop: 64, paddingBottom: 96 }}>
      {/* Section header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 40 }}>
        <div>
          <p className="fs-section-label" style={{ marginBottom: 8 }}>The apps</p>
          <h2 style={{
            fontFamily: 'var(--fs-font-headline)',
            fontWeight: 'var(--fs-weight-bold)' as any,
            fontSize: 32,
            color: 'var(--fs-text)',
            letterSpacing: 'var(--fs-tracking-tight)',
          }}>Your AI-powered design suite</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, overflow: 'auto', paddingBottom: 4, flexShrink: 0 }}>
          {filters.map(f => (
            <button
              key={f.value}
              className={`fs-chip${filter === f.value ? ' fs-chip--active' : ''}`}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Decision System */}
      {showDecision && (
        <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--fs-decision)' }} />
              <span className="fs-section-label" style={{ color: 'var(--fs-decision)' }}>Decision System</span>
              <span style={{ fontSize: 11, color: 'var(--fs-text-muted)', fontFamily: 'var(--fs-font-body)', marginLeft: 4 }}>
                - visualize, explore, present
              </span>
            </div>
          </div>
          <div className="fs-app-grid" style={{ marginBottom: 56 }}>
            {DECISION_APPS.map(app => (
              <AppCard key={app.id} app={app} isLoggedIn={isLoggedIn} onOpenAuth={onOpenAuth} />
            ))}
          </div>
        </>
      )}

      {/* Production System */}
      {showProduction && (
        <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--fs-production)' }} />
              <span className="fs-section-label" style={{ color: 'var(--fs-production)' }}>Production System</span>
              <span style={{ fontSize: 11, color: 'var(--fs-text-muted)', fontFamily: 'var(--fs-font-body)', marginLeft: 4 }}>
                - modify, export, print-ready
              </span>
            </div>
          </div>
          <div className="fs-app-grid">
            {PRODUCTION_APPS.map(app => (
              <AppCard key={app.id} app={app} isLoggedIn={isLoggedIn} onOpenAuth={onOpenAuth} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
